import React, { useState, useRef, useCallback } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { eventDonationFieldInteraction, eventFormFieldInteraction, PROTECT_FIELD_VALUE } from "../../utilities/dataLayer";

import useDonateRenewCardDeails from "../../hooks/useFormDonateRenewCardDetails";
import useFormCsrfToken from "../../hooks/useFormCsrfToken";

import TextInput from "../inputs/TextInput";
import { tokenizeFatZebraCard } from "../../utilities/fatZebraTokenize";
import { buildRenewFormPostSnapshot, buildRenewPayload } from "../../utilities/renewPayloadBuilder";
import { isFatZebraGateway } from "../../utilities/paymentGateway";

const redirectToUrl = (url, token) => {
  if (url) {
    // Check if the URL already has query parameters
    const delimiter = url.includes("?") ? "&" : "?";
    // Redirect to the new URL with the token appended
    window.location.replace(`${url}${delimiter}token=${token}`);
  }
};

const getActiveFormInputs = (form) => {
  const formData = [...form];

  return formData
    .map((element) => ({
      uniqueId: element.id,
      isValid: element.dataset.isValidate,
      disabled: element.disabled,
      value: element.value,
    }))
    .filter((element) => element.disabled === false && element.uniqueId !== "Addadollaramount" && element.uniqueId !== "");
};

const FormError = ({ status }) => {
  switch (status) {
    //update timeout
    case "422":
      return (
        <div className="renew-page__error-paragraph">
          <p className="renew-page__error-msg">
            Unfortunately your attempt to update your donation details has <strong>NOT been successful</strong>.
          </p>
          <p>
            We value your ongoing support. Please call Starlight on 1300 727 827 and we can update your details over the phone. Our team is available
            to assist you Monday to Friday 9am – 5pm AEST, excluding public holidays.{" "}
          </p>
          <p>Alternatively please try again using your personalised link in the email you received from Starlight.</p>
        </div>
      );
    default:
      return <p className="renew-page__error-text">There is an error, please try again later.</p>;
  }
};

const TokenError = ({ status }) => {
  switch (status) {
    //If someone has already updated their details and attempts to update them again, please present them with the following message
    //token completed
    case 409:
      return (
        <div className="renew-page__error-paragraph">
          <p>Your donation details have already been updated.</p>
          <p>Please contact Starlight on 1300 727 827 if you believe there is an issue.</p>
        </div>
      );
    //token expired
    case 401:
      return (
        <div className="renew-page__error-paragraph">
          <p className="renew-page__error-text">This link has expired.</p>
          <p>
            For security, these links are only valid for a short duration. Please contact Starlight on 1300 727 827 to be issued a new link or to
            update your donation details via phone. Our team is available to assist you Monday to Friday 9am – 5pm AEST, excluding public holidays.
          </p>
        </div>
      );
    //token invalid
    case 500:
    case 400:
    default:
      return (
        <div className="renew-page__error-paragraph">
          <p className="renew-page__error-text">Hmmm looks like that is not a valid link to update your donation details.</p>
          <p>
            Please contact Starlight on 1300 727 827 to be issued a new link or to update your donation details via phone. Our team is available to
            assist you Monday to Friday 9am – 5pm AEST, excluding public holidays.
          </p>
        </div>
      );
  }
};

const DonateRenewForm = ({ successPage = null, failPage = null, paymentGateway }) => {
  const isBrowser = () => typeof window !== "undefined"; //gatsby doesn't like the window object when build, but it's fine after building it
  let formSubmitSuccess, formSubmitError;
  let token = null;
  if (isBrowser()) {
    const urlParams = new URLSearchParams(window.location.search);
    token = urlParams.get("token");
    formSubmitSuccess = urlParams.get("success");
    formSubmitError = urlParams.get("error") ? "Donation update failed, please try again later." : null;
  }

  const [isFormValid, setIsFormValid] = useState(false);
  const [stickyHoney, setStickyHoney] = useState("");
  const [isOnce, setOnce] = useState(true);
  const [formError, setFormError] = useState(formSubmitError ?? null);
  const [formSuccess, setFormSuccess] = useState(formSubmitSuccess ?? null);
  const [formPost, setFormPost] = useState({});
  const csrfToken = useFormCsrfToken();
  const [monthlyDonationDetails, isMonthlyDonationDetailsValid, statusCode] = useDonateRenewCardDeails({ token: token });
  const isFatZebraRenewal = isFatZebraGateway(paymentGateway);

  //recaptcha related functions
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log("Execute recaptcha not yet available");
      return;
    }
    // Do whatever you want with the token
    return await executeRecaptcha("subscription");
  }, [executeRecaptcha]);

  const handleVerifyAndSubmit = async (event) => {
    event.preventDefault();
    const gRecaptchaResponse = await handleReCaptchaVerify();
    if (!gRecaptchaResponse) return;
    handleSubmit(gRecaptchaResponse);
  };

  const handleSubmit = async (gRecaptchaResponse) => {
    if (isOnce === true) {
      setOnce(false);
    }

    try {
      const currentFormPost = getCurrentFormPost();
      let finalFormPost = currentFormPost;

      if (isFatZebraRenewal) {
        try {
          const tokenizedCard = await tokenizeFatZebraCard({
            formEndpoint: process.env.GATSBY_FORM_ENDPOINT,
            cardDetails: {
              cardNumber: currentFormPost.card_number,
              cardHolder: currentFormPost.name_on_card,
              expiryMonth: currentFormPost.expiry_month,
              expiryYear: currentFormPost.expiry_year,
              cvv: currentFormPost.ccv,
              cardType: currentFormPost.card_type,
            },
          });

          finalFormPost = {
            ...currentFormPost,
            card_token: tokenizedCard.cardToken,
            card_last4: tokenizedCard.cardLast4,
            card_type: tokenizedCard.cardType,
            name_on_card: tokenizedCard.nameOnCard,
            expiry_month: tokenizedCard.expiryMonth,
            expiry_year: tokenizedCard.expiryYear,
          };
        } catch (error) {
          const errorMessage = error?.message ?? "Unable to securely validate card details.";
          setFormError(errorMessage);
          if (failPage) {
            redirectToUrl(failPage, token);
          }
          setOnce(true);
          return;
        }
      }

      const response = await fetch(`${process.env.GATSBY_FORM_ENDPOINT}/card/renew`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...buildRenewPayload({
            monthlyDonationDetails,
            formPost: finalFormPost,
            csrfToken,
            stickyHoney,
            paymentGateway,
          }),
          "g-recaptcha-response": gRecaptchaResponse,
        }),
      });

      if (response.status === 200) {
        if (successPage) {
          redirectToUrl(successPage, token);
        } else {
          setFormSuccess(true);
        }
        formSubmitError = null;
        setFormError(null);
      } else {
        formSubmitSuccess = null;
        setFormSuccess(null);
        if (response.status) {
          setFormError(<FormError status={response.status} />);
        } else {
          setFormError(<FormError />);
        }
        if (failPage) redirectToUrl(failPage, token);
      }
    } catch (error) {
      console.error(error);
      formSubmitSuccess = null;
      setFormSuccess(null);
      setFormError("There is an error, please try again later.");
      if (failPage) redirectToUrl(failPage, token);
    }

    setOnce(true);
  };

  const validateForm = () => {
    if (!formElement.current) return;
    const formInputs = getActiveFormInputs(formElement.current);
    const tempFormPost = buildRenewFormPostSnapshot(formInputs);
    setIsFormValid(formInputs.every((input) => input.isValid === "true")); //set form valid if all input is valid
    setFormPost(tempFormPost);
  };

  const formElement = useRef();

  const getCurrentFormPost = () => {
    if (!formElement.current) {
      return { ...formPost };
    }

    return buildRenewFormPostSnapshot(getActiveFormInputs(formElement.current));
  };

  // const handleClick = (event) => {
  //   const target = event.target;
  //   if (target.tagName.toLowerCase() === "button" && target.parentNode.hasAttribute("selectorgroup")) {
  //     eventDonationFieldInteraction(event, "Renew Donation", "Donation form");
  //   }
  // };

  const handleFieldBlur = (event, name, isTextInput = false) => {
    let fieldValue;
    if (isTextInput) {
      fieldValue = PROTECT_FIELD_VALUE;
    } else if (typeof event?.target.checked !== "undefined") {
      fieldValue = event.target.checked ? "checked" : "unchecked";
    } else {
      fieldValue = event?.target.value;
    }
    eventFormFieldInteraction(name, "subscription form", name, fieldValue);
  };

  if (formSuccess) {
    return (
      <div className="renew-page renew-page__container">
        <div className="renew-page__success-paragraph">
          <p className="renew-page__success-text">Your donation details have been successfully updated.</p>
          <p>
            Thank you for your continued support of Starlight in helping to brighten the lives of seriously ill and hospitalised children. Learn more
            about how your ongoing support helps by visiting the{" "}
            <a href="https://www.starlight.org.au/about-us/what-we-do/our-impact">Our impact page.</a>
          </p>
          <p>
            If you require any further assistance please contact us by calling 1300 727 827. Our Supporter Experience Team is available Monday to
            Friday 9am – 5pm AEST, excluding public holidays. Our you can leave us a message via our{" "}
            <a href="https://www.starlight.org.au/contact-us">Contact Us</a> page.
          </p>
          <p>
            To explore more about Starlight Children’s Foundation start with our <a href="https://www.starlight.org.au/">home page</a>
          </p>
        </div>
      </div>
    );
  }

  if (!isMonthlyDonationDetailsValid()) {
    return (
      <div className="renew-page renew-page__container">
        <TokenError status={statusCode} />
      </div>
    );
  }

  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
  return (
    <div className="renew-page renew-page__container">
      <p className="renew-page__text">
        Your <b>current</b> monthly donation details:
      </p>
      <table className="renew-page__table">
        <tbody>
          <tr>
            <th>Cardholder name: </th>
            <td>{monthlyDonationDetails.name_on_card}</td>
          </tr>
          <tr>
            <th>Card type: </th>
            <td>{monthlyDonationDetails.card_type}</td>
          </tr>
          <tr>
            <th>Last 4 digits of current card: </th>
            <td>{monthlyDonationDetails.card_number}</td>
          </tr>
          <tr>
            <th>Expiry date: </th>
            <td>{monthlyDonationDetails.expiry_date}</td>
          </tr>
          <tr>
            <th>Monthly gift amount: </th>
            <td>
              {!isNaN(monthlyDonationDetails.amount) ? "$" : ""}
              {monthlyDonationDetails.amount}
            </td>
          </tr>
        </tbody>
      </table>

      <form
        className={`renew-page__form ${!isMonthlyDonationDetailsValid() || formSuccess ? "hide" : ""}`}
        ref={formElement}
        data-payment-gateway={paymentGateway}
        onKeyUp={validateForm}
        onSubmit={handleVerifyAndSubmit}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        // onClick={handleClick}
      >
        <input
          value={stickyHoney}
          type="text"
          name="a_password"
          style={{ display: "none" }}
          tabIndex="-1"
          autoComplete="off"
          onChange={(e) => setStickyHoney(e.target.value)}
        />

        <p className="renew-page__text">
          Please enter your <b>updated card details</b> below:
        </p>

        <div className="form-group donation-form">
          <TextInput
            name="Cardholder name"
            type="text"
            required={true}
            disabled={false}
            key={"card-name"}
            validation={[]}
            validationShow={true}
            msgValidation={"Please enter the name as it appears on your card"}
            showValidaiton={false}
            uniqueId={"card-name"}
            options={null}
            onBlurChanged={handleFieldBlur}
          />

          <TextInput
            name="Card number"
            type="text"
            required={true}
            disabled={false}
            key="card-number"
            validation={[]}
            validationShow={true}
            msgValidation="Please enter a card number"
            showValidaiton={false}
            uniqueId="card-number"
            options={null}
            onBlurChanged={handleFieldBlur}
          />

          <TextInput
            name={"Expiry date"}
            type="text"
            required={true}
            disabled={false}
            key="expiry-date"
            validation={null}
            validationShow={false}
            msgValidation="Please enter expiry date in the format MMYY"
            showValidaiton={true}
            uniqueId="expiry-date"
            onBlurChanged={handleFieldBlur}
          />

          <TextInput
            name={"CVV"}
            type="text"
            required={true}
            disabled={false}
            key="cvv"
            validation={null}
            validationShow={false}
            msgValidation="Please enter the Card Verification Value (CVV) which is a three or four digit number usually located on the back of your card."
            showValidaiton={false}
            uniqueId="cvv"
            onBlurChanged={handleFieldBlur}
          />
        </div>

        {formError ? <p className="renew-page__error">{formError}</p> : ""}
        <p className="renew-page__muted-text">Donations are tax deductible. Registered with ACNC.</p>
        <div className="renew-page__secure-icon">
          <p className="renew-page__secure-icon-text">This is a secure form</p>
          <div className="lock-icon">
            <img src="//images.ctfassets.net/5c0byp1iji8d/1Ea4w0YE0AH6rFqOX782Em/ebd94f03fcff315da5c0530ed6e21717/icon_padlock.png" alt="lock icon" />
          </div>
        </div>
        <button
          type="submit"
          className={isFormValid ? `btn btn-donation` : "btn btn-disabled"}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleVerifyAndSubmit();
            }
          }}
        >
          {isOnce ? "Submit" : "Processing..."}
        </button>
      </form>
    </div>
  );
  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
};

const WrapWithGoogleRecaptcha = ({ successPage = null, failPage = null, paymentGateway }) => {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={`${process.env.GATSBY_RECAPTCHA_PUBLIC_SITE_KEY_V3}`}>
      <DonateRenewForm successPage={successPage} failPage={failPage} paymentGateway={paymentGateway} />
    </GoogleReCaptchaProvider>
  );
};

export default WrapWithGoogleRecaptcha;
